import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  H2, 
  H3,
  Card, 
  Button, 
  Collapse,
  Tag,
  Intent,
  Callout,
  HTMLSelect,
  Dialog,
  DialogBody,
  DialogFooter,
  InputGroup
} from '@blueprintjs/core';
import { subscribeToActHistory, deleteActFromHistory, updateActDate, updatePlayerName } from '../firebase/actService';
import { fetchMultipleEloHistories } from '../firebase/eloService';
import { database } from '../firebase/config';
import { ref, set } from 'firebase/database';
import { TOP_BRACKET_CUPS as CUPS, CUP_EMOJIS, placeToPoints } from '../constants/cups';

const HISTORY_KEY = 'ACT_SCORECARD_HISTORY';
const HISTORY_PATH = 'act-history';
const TEAMS = ['A', 'B', 'C', 'D'];
const INITIAL_VISIBLE_ACTS = 5;
const VISIBLE_ACT_CHUNK = 5;

const getEntryTimestamp = (entry) => {
  const parsedDate = Date.parse(entry?.date || '');
  if (Number.isFinite(parsedDate)) {
    return parsedDate;
  }

  const numericTimestamp = Number(entry?.timestamp);
  if (Number.isFinite(numericTimestamp) && numericTimestamp > 0) {
    return numericTimestamp;
  }

  return 0;
};

const getRosterPlayers = (entry) => {
  const players = [];

  TEAMS.forEach((team) => {
    const roster = entry?.teams?.[team]?.players || {};
    ['A', 'B', 'C'].forEach((slot) => {
      const name = roster[slot];
      if (typeof name === 'string' && name.trim()) {
        players.push(name.trim());
      }
    });
  });

  return [...new Set(players)];
};

const getLastEntryAtOrBefore = (entries, timestamp) => {
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i].at <= timestamp) {
      return entries[i];
    }
  }
  return null;
};

function History() {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [eloDeltaByAct, setEloDeltaByAct] = useState({});
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [dateFilter, setDateFilter] = useState(null);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('latest'); // 'latest' or 'oldest'
  const [editingPlayer, setEditingPlayer] = useState(null); // Format: { entryId, team, player }
  const [editingPlayerName, setEditingPlayerName] = useState('');
  const [editingEntry, setEditingEntry] = useState(null); // ID of entry being edited
  const [editedData, setEditedData] = useState(null); // Temporary edited data
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ACTS);
  const listContainerRef = useRef(null);
  const playerHistoryCacheRef = useRef({});
  
  // Admin mode state
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');

  const visibleHistory = useMemo(
    () => filteredHistory.slice(0, visibleCount),
    [filteredHistory, visibleCount]
  );
  const hasMoreVisibleHistory = visibleCount < filteredHistory.length;

  const loadMoreVisibleHistory = useCallback(() => {
    setVisibleCount((previousCount) =>
      Math.min(previousCount + VISIBLE_ACT_CHUNK, filteredHistory.length)
    );
  }, [filteredHistory.length]);

  // Check for admin mode on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('admin') === 'true') {
      setIsAdminModalOpen(true);
    }
  }, []);

  useEffect(() => {
    playerHistoryCacheRef.current = {};
  }, [history]);

  // Load per-ACT ELO changes for each player (derived from ELO_history timestamps)
  useEffect(() => {
    let isCancelled = false;

    const loadVisibleEloDeltas = async () => {
      if (history.length === 0 || visibleHistory.length === 0) {
        setEloDeltaByAct({});
        return;
      }

      const visibleEntryIds = new Set(visibleHistory.map((entry) => entry.id));
      const visiblePlayers = [...new Set(visibleHistory.flatMap(getRosterPlayers))];

      if (visiblePlayers.length === 0) {
        setEloDeltaByAct({});
        return;
      }

      try {
        const missingPlayers = visiblePlayers.filter(
          (playerName) => !(playerName in playerHistoryCacheRef.current)
        );

        if (missingPlayers.length > 0) {
          const fetchedPlayerHistories = await fetchMultipleEloHistories(missingPlayers);
          if (isCancelled) return;

          missingPlayers.forEach((playerName) => {
            const entries = fetchedPlayerHistories?.[playerName] || [];
            playerHistoryCacheRef.current[playerName] = entries
              .map((entry) => ({
                at: Number(entry.at),
                value: Number(entry.value)
              }))
              .filter((entry) => Number.isFinite(entry.at) && Number.isFinite(entry.value))
              .sort((a, b) => a.at - b.at);
          });
        }

        const chronologicalActs = [...history]
          .map((entry, index) => ({
            entry,
            index,
            ts: getEntryTimestamp(entry)
          }))
          .sort((a, b) => {
            if (a.ts !== b.ts) return a.ts - b.ts;
            return a.index - b.index;
          });

        const nextEloDeltaByAct = {};

        chronologicalActs.forEach((act, idx) => {
          if (!visibleEntryIds.has(act.entry.id)) {
            return;
          }

          const nextAct = chronologicalActs[idx + 1];
          const windowStart = act.ts;
          const windowEnd = nextAct ? nextAct.ts : Number.POSITIVE_INFINITY;
          const playerNames = getRosterPlayers(act.entry);
          const actDeltas = {};

          playerNames.forEach((playerName) => {
            const playerHistory = playerHistoryCacheRef.current[playerName];
            if (!playerHistory || playerHistory.length === 0) {
              return;
            }

            const beforeAct = getLastEntryAtOrBefore(playerHistory, windowStart);
            const entriesDuringAct = playerHistory.filter(
              (entry) => entry.at > windowStart && entry.at < windowEnd
            );

            if (entriesDuringAct.length === 0) {
              actDeltas[playerName] = 0;
              return;
            }

            const baseline = beforeAct ? beforeAct.value : entriesDuringAct[0].value;
            const afterAct = entriesDuringAct[entriesDuringAct.length - 1].value;
            actDeltas[playerName] = Math.round(afterAct - baseline);
          });

          nextEloDeltaByAct[act.entry.id] = actDeltas;
        });

        if (!isCancelled) {
          setEloDeltaByAct(nextEloDeltaByAct);
        }
      } catch (error) {
        console.error('Error loading per-ACT ELO deltas:', error);
        if (!isCancelled) {
          setEloDeltaByAct({});
        }
      }
    };

    loadVisibleEloDeltas();

    return () => {
      isCancelled = true;
    };
  }, [history, visibleHistory]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ACTS);
  }, [filteredHistory]);

  const handleHistoryScroll = useCallback((event) => {
    if (!hasMoreVisibleHistory) {
      return;
    }

    const target = event.currentTarget;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceToBottom <= 250) {
      loadMoreVisibleHistory();
    }
  }, [hasMoreVisibleHistory, loadMoreVisibleHistory]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || !hasMoreVisibleHistory) {
      return;
    }

    if (container.scrollHeight <= container.clientHeight + 40) {
      loadMoreVisibleHistory();
    }
  }, [visibleHistory, hasMoreVisibleHistory, loadMoreVisibleHistory]);

  useEffect(() => {
    // Subscribe to Firebase real-time updates
    const unsubscribe = subscribeToActHistory((firebaseHistory) => {
      console.log('History received from Firebase:', firebaseHistory.length, 'entries');
      console.log('Firebase history data:', firebaseHistory);
      setHistory(firebaseHistory);
      setFilteredHistory(firebaseHistory);
    });

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Keep this function for potential fallback to localStorage
  const loadHistory = () => {
    const savedHistory = localStorage.getItem(HISTORY_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(parsedHistory);
        setFilteredHistory(parsedHistory);
      } catch (error) {
        console.error('Error loading history:', error);
        setHistory([]);
        setFilteredHistory([]);
      }
    }
  };

  // Filter and sort history based on date, period filters, and sort order
  useEffect(() => {
    let filtered = [...history];

    // Apply date/period filters
    if (periodFilter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      filtered = history.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= today && entryDate < tomorrow;
      });
    } else if (periodFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      filtered = history.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= weekAgo;
      });
    } else if (periodFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      
      filtered = history.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= monthAgo;
      });
    } else if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filterDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(filterDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      filtered = history.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= filterDate && entryDate < nextDay;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (sortOrder === 'latest') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });

    setFilteredHistory(filtered);
  }, [history, dateFilter, periodFilter, sortOrder]);

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleDateChange = async (entry, newDate) => {
    if (!isAdminMode) {
      alert('Admin access required to change dates.');
      return;
    }
    
    try {
      console.log('📅 Updating ACT date:', entry.id, 'to', newDate);
      console.log('📅 Full entry context:', entry);
      
      // Update in Firebase
      const success = await updateActDate(entry.id, newDate);
      
      if (success) {
        // Also update in localStorage as backup
        const localHistory = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
        const updatedHistory = localHistory.map(item => 
          item.id === entry.id ? { ...item, date: new Date(newDate).toISOString() } : item
        );
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updatedHistory));
        
        console.log('✅ ACT date updated successfully');
        // Note: The UI will update automatically via Firebase subscription
      } else {
        console.log('⚠️ Date update returned false - entry not found');
        alert('Failed to update ACT date - entry not found in database.');
      }
    } catch (error) {
      console.error('❌ Error updating ACT date:', error);
      alert(`Error updating ACT date: ${error.message}`);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!isAdminMode) {
      alert('Admin access required to delete entries.');
      return;
    }
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ACT from ${formatDate(entry.date)}?\n\nThis action cannot be undone and will remove the data from both the app and Firebase database.`
    );
    
    if (confirmDelete) {
      try {
        console.log('🗑️ Deleting ACT entry:', entry.id);
        
        // Delete from Firebase
        await deleteActFromHistory(entry.id);
        
        // Also remove from localStorage as backup
        const localHistory = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
        const updatedHistory = localHistory.filter(item => item.id !== entry.id);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updatedHistory));
        
        console.log('✅ ACT entry deleted successfully');
        // Note: The UI will update automatically via Firebase subscription
      } catch (error) {
        console.error('❌ Error deleting ACT entry:', error);
        alert(`Error deleting ACT entry: ${error.message}`);
      }
    }
  };

  const handleStartEditingPlayer = (entryId, team, player, currentName) => {
    if (!isAdminMode) {
      alert('Admin access required to edit player names.');
      return;
    }
    setEditingPlayer({ entryId, team, player });
    setEditingPlayerName(currentName);
  };

  const handleSavePlayerName = async () => {
    if (!editingPlayer || !editingPlayerName.trim()) return;
    
    if (!isAdminMode) {
      alert('Admin access required to save changes.');
      return;
    }
    
    try {
      console.log('✏️ Updating player name:', editingPlayer, 'to:', editingPlayerName);
      console.log('✏️ Full editing context:', {
        entryId: editingPlayer.entryId,
        team: editingPlayer.team,
        player: editingPlayer.player,
        newName: editingPlayerName.trim()
      });
      
      const success = await updatePlayerName(
        editingPlayer.entryId, 
        editingPlayer.team, 
        editingPlayer.player, 
        editingPlayerName.trim()
      );
      
      if (success) {
        console.log('✅ Player name updated successfully');
        setEditingPlayer(null);
        setEditingPlayerName('');
        
        // Show success message
        alert(`Player name updated successfully! Analytics will refresh automatically.`);
      } else {
        console.log('⚠️ Update returned false - entry not found');
        alert('Failed to update player name - entry not found in database.');
      }
      
      // Note: The UI will update automatically via Firebase subscription
      // The Analytics page will also update automatically since it subscribes to the same Firebase data
    } catch (error) {
      console.error('❌ Error updating player name:', error);

      // Provide more specific error messages
      let errorMessage = `Failed to update player name: ${error.message}`;

      if (error.message.includes('permission')) {
        errorMessage = 'Permission denied. Please check your admin access.';
      } else if (error.message.includes('not found')) {
        errorMessage = 'ACT entry not found in database.';
      } else if (error.message.includes('verification failed')) {
        errorMessage = 'Update appeared to succeed but verification failed. Please try again.';
      } else if (error.message.includes('Firebase not ready')) {
        errorMessage = 'Database connection error. Please check your internet connection.';
      }

      alert(`❌ ${errorMessage}\n\nCheck the browser console for detailed error information.`);

      // Reset the editing state on error
      setEditingPlayer(null);
      setEditingPlayerName('');
    }
  };

  const handleCancelEditingPlayer = () => {
    setEditingPlayer(null);
    setEditingPlayerName('');
  };

  const handleStartEditingEntry = (entry) => {
    if (!isAdminMode) {
      alert('Admin access required to edit entries.');
      return;
    }
    setEditingEntry(entry.id);
    setEditedData(JSON.parse(JSON.stringify(entry))); // Deep clone
  };

  const handleCancelEditingEntry = () => {
    setEditingEntry(null);
    setEditedData(null);
  };

  const handleRacePlaceChange = (cup, raceNum, team, newPlace) => {
    if (!editedData) return;
    
    const updatedData = { ...editedData };
    if (!updatedData.races[cup]) updatedData.races[cup] = {};
    if (!updatedData.races[cup][raceNum]) updatedData.races[cup][raceNum] = {};
    if (!updatedData.races[cup][raceNum][team]) updatedData.races[cup][raceNum][team] = {};
    
    // Parse place as number or 'X' for void
    const place = newPlace === 'X' || newPlace === 'x' ? 'X' : parseInt(newPlace);
    updatedData.races[cup][raceNum][team].place = place;
    
    // Recalculate totals
    const newTotals = {};
    TEAMS.forEach(t => {
      let total = 0;
      CUPS.forEach(c => {
        if (updatedData.races[c]) {
          [1, 2, 3, 4].forEach(r => {
            if (updatedData.races[c][r] && updatedData.races[c][r][t]) {
              const p = updatedData.races[c][r][t].place;
              if (p !== 'X' && typeof p === 'number') {
                total += placeToPoints(p);
              }
            }
          });
        }
      });
      newTotals[t] = total;
    });
    updatedData.totals = newTotals;
    
    setEditedData(updatedData);
  };

  const handleSaveEditedEntry = async () => {
    if (!editedData || !editingEntry) return;
    
    if (!isAdminMode) {
      alert('Admin access required to save changes.');
      return;
    }
    
    try {
      
      console.log('💾 Saving edited ACT data:', editingEntry);
      
      // Update in Firebase
      const entryRef = ref(database, `${HISTORY_PATH}/${editingEntry}`);
      await set(entryRef, editedData);
      
      console.log('✅ ACT data updated successfully');
      alert('ACT data has been updated successfully!');
      
      setEditingEntry(null);
      setEditedData(null);
    } catch (error) {
      console.error('❌ Error saving edited ACT data:', error);
      alert(`Error saving changes: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderEditablePlayerName = (entry, team, player, currentName, options = {}) => {
    const {
      color = 'var(--accent-primary)',
      fontWeight = 'inherit'
    } = options;
    const isEditing = editingPlayer && 
      editingPlayer.entryId === entry.id && 
      editingPlayer.team === team && 
      editingPlayer.player === player;

    if (isEditing) {
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
          <input
            type="text"
            value={editingPlayerName}
            onChange={(e) => setEditingPlayerName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSavePlayerName();
              if (e.key === 'Escape') handleCancelEditingPlayer();
            }}
            autoFocus
            style={{
              padding: '2px 6px',
              fontSize: '0.8rem',
              border: '1px solid var(--accent-primary)',
              borderRadius: '3px',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              width: '80px'
            }}
          />
          <button
            onClick={handleSavePlayerName}
            style={{
              padding: '2px 6px',
              fontSize: '0.7rem',
              backgroundColor: 'var(--success)',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
            title="Save"
          >
            ✓
          </button>
          <button
            onClick={handleCancelEditingPlayer}
            style={{
              padding: '2px 6px',
              fontSize: '0.7rem',
              backgroundColor: 'var(--danger)',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
            title="Cancel"
          >
            ✕
          </button>
        </div>
      );
    }

    return (
      <span
        onClick={() => handleStartEditingPlayer(entry.id, team, player, currentName)}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          color,
          fontWeight
        }}
        title="Click to edit player name"
      >
        {currentName}
      </span>
    );
  };

  const getNamedTeamRoster = (entry, team) => {
    const players = entry.teams?.[team]?.players || {};

    return ['A', 'B', 'C'].reduce((roster, slot) => {
      const rawName = players[slot];
      const placeholder = `Player ${slot}`;
      const trimmedName = typeof rawName === 'string' ? rawName.trim() : '';

      if (!trimmedName || trimmedName === placeholder) {
        return roster;
      }

      roster.push({ slot, name: trimmedName });
      return roster;
    }, []);
  };

  const renderTeamRoster = (
    entry,
    team,
    {
      layout = 'stacked',
      labelColor = 'var(--text-secondary)',
      nameColor = 'var(--text-primary)',
      nameFontSize = '0.9rem',
      nameFontWeight = 600
    } = {}
  ) => {
    const roster = getNamedTeamRoster(entry, team);

    if (roster.length === 0) {
      return `Team ${team}`;
    }

    if (layout === 'inline') {
      return roster.map(({ slot, name }, index) => (
        <React.Fragment key={`${team}-${slot}`}>
          {index > 0 ? <span style={{ color: labelColor }}> • </span> : null}
          <span style={{ color: labelColor, fontWeight: 700 }}>{slot}:</span>{' '}
          {renderEditablePlayerName(entry, team, slot, name, {
            color: nameColor,
            fontWeight: nameFontWeight
          })}
        </React.Fragment>
      ));
    }

    return (
      <div style={{ display: 'grid', gap: '0.3rem' }}>
        {roster.map(({ slot, name }) => (
          <div
            key={`${team}-${slot}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '18px 1fr',
              alignItems: 'start',
              gap: '0.35rem'
            }}
          >
            <span style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: labelColor,
              paddingTop: '0.08rem'
            }}>
              {slot}
            </span>
            <div style={{ fontSize: nameFontSize, lineHeight: 1.35 }}>
              {renderEditablePlayerName(entry, team, slot, name, {
                color: nameColor,
                fontWeight: nameFontWeight
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getWinnerTeam = (entry) => {
    let maxScore = -1;
    let winners = [];
    
    TEAMS.forEach(team => {
      const score = entry.totals[team] || 0;
      if (score > maxScore) {
        maxScore = score;
        winners = [team];
      } else if (score === maxScore) {
        winners.push(team);
      }
    });
    
    return { winners, score: maxScore };
  };

  const ordinal = (num) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };
  
  // Admin authentication handlers
  const handleAdminPasswordSubmit = () => {
    if (adminPassword === process.env.REACT_APP_ADMIN_PASSWORD) {
      setIsAdminMode(true);
      setIsAdminModalOpen(false);
      setAdminPassword('');
    } else {
      alert('Incorrect password. Access denied.');
      setAdminPassword('');
    }
  };
  
  const closeAdminModal = () => {
    setIsAdminModalOpen(false);
    setAdminPassword('');
    // Remove admin parameter from URL
    const url = new URL(window.location);
    url.searchParams.delete('admin');
    window.history.replaceState({}, '', url);
  };

  const getPlaceFromPoints = (points) => {
    if (points === 'X') return 'X';
    // Standard scoring: 3 points = 1st place, 0 points = 4th place
    const placeMap = { 3: 1, 2: 2, 1: 3, 0: 4 };
    return placeMap[points] || 4;
  };

  const formatSignedDelta = (delta) => {
    if (!Number.isFinite(delta)) return 'N/A';
    if (delta > 0) return `+${delta}`;
    return `${delta}`;
  };

  const getPlayerActPoints = (entryData) => {
    const playerPoints = {};

    if (!entryData?.races) {
      return playerPoints;
    }

    Object.values(entryData.races).forEach((cupRaces) => {
      Object.values(cupRaces || {}).forEach((race) => {
        TEAMS.forEach((team) => {
          const raceData = race?.[team];
          if (!raceData) return;

          const racerName = typeof raceData.racer === 'string' ? raceData.racer.trim() : '';
          const place = raceData.place;

          if (!racerName || place === 'X' || !Number.isFinite(Number(place))) {
            return;
          }

          const points = placeToPoints(Number(place));
          playerPoints[racerName] = (playerPoints[racerName] || 0) + points;
        });
      });
    });

    return playerPoints;
  };

  if (history.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <H2 style={{ margin: '0 0 1rem 0' }}>History</H2>
        <Callout intent={Intent.PRIMARY} title="No ACTs Found">
          Complete some ACT scorecards to see them appear here!
        </Callout>
      </div>
    );
  }

  return (
    <div
      ref={listContainerRef}
      onScroll={handleHistoryScroll}
      style={{ height: '100%', overflow: 'auto' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <H2 style={{ margin: 0 }}>ACT History</H2>
        <Tag intent={Intent.PRIMARY}>{filteredHistory.length} of {history.length} ACT{history.length !== 1 ? 's' : ''}</Tag>
        {filteredHistory.length > 0 && (
          <Tag minimal>
            Showing {Math.min(visibleHistory.length, filteredHistory.length)} loaded
          </Tag>
        )}
        {isAdminMode && (
          <Tag intent={Intent.SUCCESS} icon="lock">Admin Mode Active</Tag>
        )}
        
        {/* Filter Controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Period:</span>
            <HTMLSelect
              value={periodFilter}
              onChange={(e) => {
                setPeriodFilter(e.target.value);
                if (e.target.value !== 'custom') {
                  setDateFilter(null);
                }
              }}
              options={[
                { label: 'All Time', value: 'all' },
                { label: 'Today', value: 'today' },
                { label: 'Past Week', value: 'week' },
                { label: 'Past Month', value: 'month' },
                { label: 'Specific Date', value: 'custom' }
              ]}
              style={{ minWidth: '120px' }}
            />
          </div>
          
          {periodFilter === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Date:</span>
              <input
                type="date"
                value={dateFilter ? dateFilter.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateFilter(e.target.value ? new Date(e.target.value) : null)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '3px',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  width: '150px'
                }}
              />
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Sort:</span>
            <HTMLSelect
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              options={[
                { label: 'Latest First', value: 'latest' },
                { label: 'Oldest First', value: 'oldest' }
              ]}
              style={{ minWidth: '120px' }}
            />
          </div>
        </div>
      </div>
      
      {filteredHistory.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Callout intent={Intent.NONE} title="No ACTs Found">
            {periodFilter === 'all' 
              ? 'Complete some ACT scorecards to see them appear here!'
              : 'No ACTs found for the selected time period. Try adjusting your filters.'
            }
          </Callout>
        </div>
      ) : (
      <div style={{ display: 'grid', gap: '0.85rem' }}>
          {visibleHistory.map((entry) => {
          const { winners, score } = getWinnerTeam(entry);
          const isExpanded = expandedItems.has(entry.id);
          const entryEloDeltas = eloDeltaByAct[entry.id] || {};
          const pointsSourceEntry = editingEntry === entry.id && editedData ? editedData : entry;
          const entryPlayerActPoints = getPlayerActPoints(pointsSourceEntry);
          const winnerSummaryStyle = winners.length === 1
            ? {
                background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.16), rgba(74, 158, 255, 0.14))',
                border: '1px solid rgba(74, 222, 128, 0.38)'
              }
            : {
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(245, 158, 11, 0.08))',
                border: '1px solid rgba(245, 158, 11, 0.34)'
              };
          const hasAnyEloDelta = TEAMS.some(team => {
            const players = entry.teams?.[team]?.players || {};
            return ['A', 'B', 'C'].some(slot => {
              const name = players[slot];
              return typeof name === 'string' && name.trim();
            });
          });

          return (
            <Card key={entry.id} style={{ 
              padding: '1rem',
              background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))',
              border: '1px solid var(--border)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '1rem',
                flexWrap: 'wrap',
                marginBottom: '1rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.5rem' }}>
                    <H3 style={{ margin: 0, fontSize: '1.3rem', color: 'var(--accent-primary)' }}>
                      🏁 ACT
                    </H3>
                    <input
                      type="date"
                      value={new Date(entry.date).toISOString().split('T')[0]}
                      onChange={(e) => handleDateChange(entry, e.target.value)}
                      disabled={!isAdminMode}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '3px',
                        border: '1px solid var(--border)',
                        backgroundColor: isAdminMode ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '0.8rem',
                        cursor: isAdminMode ? 'pointer' : 'not-allowed',
                        opacity: isAdminMode ? 1 : 0.6
                      }}
                    />
                  </div>
                  <div style={{ display: 'none' }}>
                    {winners.length === 1 ? (
                      <Tag intent={Intent.SUCCESS} large style={{ padding: '0.5rem 1rem' }}>
                        🏆 Champion: {(() => {
                          const team = winners[0];
                          const players = entry.teams[team]?.players || {};
                          const playerA = players.A || 'Player A';
                          const playerB = players.B || 'Player B';
                          const playerC = players.C;
                          
                          if (playerC && playerC !== '') {
                            // 12-person ACT
                            return (
                              <span>
                                A: {renderEditablePlayerName(entry, team, 'A', playerA)}, 
                                B: {renderEditablePlayerName(entry, team, 'B', playerB)}, 
                                C: {renderEditablePlayerName(entry, team, 'C', playerC)}
                              </span>
                            );
                          } else if (playerA !== 'Player A' || playerB !== 'Player B') {
                            // 8-person ACT with custom names
                            return (
                              <span>
                                A: {renderEditablePlayerName(entry, team, 'A', playerA)} & B: {renderEditablePlayerName(entry, team, 'B', playerB)}
                              </span>
                            );
                          }
                          return `Team ${team}`;
                        })()} ({score} pts)
                      </Tag>
                    ) : (
                      <Tag intent={Intent.WARNING} large style={{ padding: '0.5rem 1rem' }}>
                        🤝 Tie: {winners.map(team => {
                          const players = entry.teams[team]?.players || {};
                          const playerA = players.A || 'Player A';
                          const playerB = players.B || 'Player B';
                          const playerC = players.C;
                          
                          if (playerC && playerC !== '') {
                            // 12-person ACT
                            return (
                              <span key={team}>
                                A: {renderEditablePlayerName(entry, team, 'A', playerA)}, 
                                B: {renderEditablePlayerName(entry, team, 'B', playerB)}, 
                                C: {renderEditablePlayerName(entry, team, 'C', playerC)}
                              </span>
                            );
                          } else if (playerA !== 'Player A' || playerB !== 'Player B') {
                            // 8-person ACT with custom names
                            return (
                              <span key={team}>
                                A: {renderEditablePlayerName(entry, team, 'A', playerA)} & B: {renderEditablePlayerName(entry, team, 'B', playerB)}
                              </span>
                            );
                          }
                          return `Team ${team}`;
                        }).reduce((prev, curr, index) => index === 0 ? [curr] : [...prev, ' & ', curr], [])} ({score} pts)
                      </Tag>
                    )}
                  </div>
                  <div style={{
                    ...winnerSummaryStyle,
                    display: 'none',
                    gap: '0.55rem',
                    padding: '0.75rem 0.9rem',
                    borderRadius: 'var(--radius)',
                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
                    maxWidth: winners.length === 1 ? 'max-content' : '100%'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--text-secondary)'
                      }}>
                        {winners.length === 1 ? 'Champion' : 'Tie'}
                      </div>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: 'var(--text-primary)'
                      }}>
                        {winners.length === 1 ? `Team ${winners[0]}` : `${winners.length} teams tied`}
                      </div>
                      <Tag
                        minimal
                        intent={winners.length === 1 ? Intent.SUCCESS : Intent.WARNING}
                        style={{ fontWeight: 700 }}
                      >
                        {score} pts
                      </Tag>
                    </div>

                    {winners.length === 1 ? (
                      <div style={{
                        fontSize: '0.88rem',
                        lineHeight: 1.45,
                        color: 'var(--text-primary)'
                      }}>
                        {renderTeamRoster(entry, winners[0], {
                          layout: 'inline',
                          labelColor: 'var(--text-secondary)',
                          nameColor: 'var(--text-primary)',
                          nameFontWeight: 700
                        })}
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.45rem' }}>
                        {winners.map(team => (
                          <div
                            key={`winner-summary-${entry.id}-${team}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '0.6rem',
                              flexWrap: 'wrap',
                              padding: '0.55rem 0.7rem',
                              borderRadius: 'var(--radius-sm)',
                              background: 'rgba(0, 0, 0, 0.14)',
                              border: '1px solid rgba(255, 255, 255, 0.06)'
                            }}
                          >
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                              Team {team}
                            </div>
                            <div style={{
                              color: 'var(--text-primary)',
                              lineHeight: 1.45,
                              flex: 1,
                              minWidth: '200px'
                            }}>
                              {renderTeamRoster(entry, team, {
                                layout: 'inline',
                                labelColor: 'var(--text-secondary)',
                                nameColor: 'var(--text-primary)',
                                nameFontWeight: 700
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    icon={isExpanded ? "chevron-up" : "chevron-down"}
                    onClick={() => toggleExpanded(entry.id)}
                    intent={Intent.PRIMARY}
                    style={{ minWidth: '120px' }}
                  >
                    {isExpanded ? 'Hide Details' : 'Show Details'}
                  </Button>
                  {isAdminMode && (
                    editingEntry === entry.id ? (
                      <>
                        <Button
                          icon="tick"
                          onClick={handleSaveEditedEntry}
                          intent={Intent.SUCCESS}
                          title="Save changes"
                        >
                          Save
                        </Button>
                        <Button
                          icon="cross"
                          onClick={handleCancelEditingEntry}
                          intent={Intent.WARNING}
                          title="Cancel editing"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          icon="edit"
                          onClick={() => handleStartEditingEntry(entry)}
                          intent={Intent.WARNING}
                          title="Edit ACT data"
                        >
                          Edit Data
                        </Button>
                        <Button
                          icon="trash"
                          onClick={() => handleDeleteEntry(entry)}
                          intent={Intent.DANGER}
                          title="Delete this ACT"
                        />
                      </>
                    )
                  )}
                </div>
              </div>

              {/* Team Totals Summary */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', 
                gap: '0.5rem', 
                marginBottom: '1rem',
                padding: '0.75rem',
                background: 'var(--bg-primary)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)'
              }}>
                {TEAMS.map(team => {
                  const isWinner = winners.includes(team);
                  return (
                    <div key={team} style={{ 
                      padding: '0.7rem',
                      background: isWinner
                        ? 'linear-gradient(135deg, rgba(74, 222, 128, 0.15), rgba(74, 158, 255, 0.12))'
                        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.01))',
                      borderRadius: 'var(--radius-sm)',
                      border: isWinner ? '1px solid rgba(74, 222, 128, 0.42)' : '1px solid var(--border)',
                      position: 'relative',
                      boxShadow: isWinner ? 'inset 0 1px 0 rgba(255, 255, 255, 0.04)' : 'none'
                    }}>
                      {isWinner && (
                        <div style={{ 
                          position: 'absolute', 
                          top: '-8px', 
                          right: '-8px', 
                          fontSize: '1.2rem' 
                        }}>
                          👑
                        </div>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.6rem',
                        marginBottom: '0.55rem'
                      }}>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: isWinner ? 'rgba(255, 255, 255, 0.74)' : 'var(--text-secondary)',
                            marginBottom: '0.2rem'
                          }}>
                            Team
                          </div>
                          <div style={{
                            fontSize: '0.92rem',
                            fontWeight: 700,
                            color: 'var(--text-primary)'
                          }}>
                            Team {team}
                          </div>
                        </div>
                        {isWinner && (
                          <div style={{
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2rem',
                            height: '2rem',
                            borderRadius: '999px',
                            background: 'rgba(255, 255, 255, 0.12)',
                            border: '1px solid rgba(255, 255, 255, 0.16)',
                            fontSize: '1rem'
                          }}>
                            ðŸ†
                          </div>
                        )}
                      </div>
                      <div style={{
                        marginBottom: '0.6rem',
                        textAlign: 'left',
                        color: 'var(--text-primary)'
                      }}>
                        {renderTeamRoster(entry, team, {
                          layout: 'stacked',
                          labelColor: isWinner ? 'rgba(255, 255, 255, 0.72)' : 'var(--text-secondary)',
                          nameColor: 'var(--text-primary)',
                          nameFontSize: '0.82rem',
                          nameFontWeight: 700
                        })}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: '0.6rem',
                        paddingTop: '0.55rem',
                        borderTop: isWinner ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.06)'
                      }}>
                        <div style={{
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: isWinner ? 'rgba(255, 255, 255, 0.72)' : 'var(--text-secondary)'
                        }}>
                          Total
                        </div>
                        <div style={{ 
                          fontSize: '1.2rem',
                          fontWeight: 800,
                          color: isWinner ? 'var(--text-primary)' : 'var(--accent-secondary)'
                        }}>
                          {editingEntry === entry.id && editedData ? editedData.totals[team] : entry.totals[team] || 0}
                          <span style={{
                            marginLeft: '0.35rem',
                            fontSize: '0.75rem',
                            color: isWinner ? 'rgba(255, 255, 255, 0.72)' : 'var(--text-secondary)'
                          }}>
                            pts
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasAnyEloDelta && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.7rem 0.85rem',
                  background: 'var(--bg-primary)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'var(--text-secondary)',
                    marginBottom: '0.55rem'
                  }}>
                    Player Summary (this ACT)
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '0.5rem'
                  }}>
                    {TEAMS.map(team => {
                      const players = entry.teams?.[team]?.players || {};
                      const playerSlots = ['A', 'B', 'C'].filter(slot => {
                        const name = players[slot];
                        return typeof name === 'string' && name.trim();
                      });

                      if (playerSlots.length === 0) {
                        return null;
                      }

                      return (
                        <div
                          key={`elo-delta-${entry.id}-${team}`}
                          style={{
                            padding: '0.5rem 0.6rem',
                            background: 'var(--bg-tertiary)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)'
                          }}
                        >
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                            Team {team}
                          </div>

                          <div style={{ display: 'grid', gap: '0.25rem' }}>
                            {playerSlots.map(slot => {
                              const rawName = players[slot];
                              const playerName = rawName.trim();
                              const delta = entryEloDeltas[playerName];
                              const actPoints = Number(entryPlayerActPoints[playerName] || 0);
                              const intent =
                                Number.isFinite(delta) && delta > 0 ? Intent.SUCCESS :
                                Number.isFinite(delta) && delta < 0 ? Intent.DANGER :
                                Intent.NONE;

                              return (
                                <div
                                  key={`elo-delta-${entry.id}-${team}-${slot}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '0.5rem'
                                  }}
                                >
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: 'var(--text-secondary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {slot}: {playerName}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                                    <Tag minimal style={{ fontWeight: 700 }}>
                                      {actPoints} pts
                                    </Tag>
                                    <Tag minimal intent={intent} style={{ fontWeight: 700 }}>
                                      {Number.isFinite(delta) ? `${formatSignedDelta(delta)} ELO` : 'ELO N/A'}
                                    </Tag>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Collapse isOpen={isExpanded}>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                  {CUPS.filter(cup => 
                    entry.races[cup] && Object.keys(entry.races[cup]).length > 0
                  ).map(cup => {
                    
                    return (
                    <div key={cup} style={{ marginBottom: '1.5rem' }}>
                      <H3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>
                        {CUP_EMOJIS[cup]} {cup} Cup
                      </H3>
                      
                      <div style={{ display: 'grid', gap: '0.5rem' }}>
                        {(() => {
                          // Determine number of races based on mode
                          const isMode12 = entry.mode === '12' || 
                            (entry.races[cup] && Object.keys(entry.races[cup]).length > 4);
                          const raceNumbers = isMode12 ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4];
                          
                          return raceNumbers.map(raceNum => {
                            // Skip race numbers that don't exist for this cup
                            if (!entry.races[cup][raceNum]) {
                              return null;
                            }
                            
                            return (
                            <div key={raceNum} style={{
                            display: 'grid',
                            gridTemplateColumns: '80px 1fr',
                            gap: '1rem',
                            padding: '0.5rem',
                            background: 'var(--bg-tertiary)',
                            borderRadius: 'var(--radius-sm)',
                            alignItems: 'center'
                          }}>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                              Race {raceNum}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                              {TEAMS.map(team => {
                                const raceData = entry.races[cup][raceNum]?.[team];
                                
                                // Skip if no race data for this team
                                if (!raceData) {
                                  return null;
                                }
                                
                                const currentEntry = editingEntry === entry.id && editedData ? editedData : entry;
                                const currentRaceData = currentEntry.races[cup][raceNum]?.[team];
                                
                                const players = currentEntry.teams[team]?.players || {};
                                const playerA = players.A || 'Player A';
                                const playerB = players.B || 'Player B';
                                const playerC = players.C || 'Player C';
                                
                                let playerName;
                                if (raceData.racer === 'A') playerName = playerA;
                                else if (raceData.racer === 'B') playerName = playerB;
                                else if (raceData.racer === 'C') playerName = playerC;
                                else playerName = raceData.racer;
                                
                                return (
                                  <div key={team} style={{
                                    textAlign: 'center',
                                    padding: '0.25rem',
                                    fontSize: '0.8rem'
                                  }}>
                                    <div style={{ fontWeight: 600 }}>Team {team}</div>
                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                      {raceData.racer}: {renderEditablePlayerName(entry, team, raceData.racer, playerName)}
                                    </div>
                                    {editingEntry === entry.id ? (
                                      <select
                                        value={currentRaceData?.place || ''}
                                        onChange={(e) => handleRacePlaceChange(cup, raceNum, team, e.target.value)}
                                        style={{
                                          padding: '2px 4px',
                                          fontSize: '0.8rem',
                                          borderRadius: '3px',
                                          border: '1px solid var(--border)',
                                          backgroundColor: 'var(--bg-primary)',
                                          color: 'var(--text-primary)',
                                          fontWeight: 700,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        <option value="1">1st</option>
                                        <option value="2">2nd</option>
                                        <option value="3">3rd</option>
                                        <option value="4">4th</option>
                                        <option value="X">VOID</option>
                                      </select>
                                    ) : (
                                      <div style={{ fontWeight: 700, color: raceData.place === 'X' ? 'var(--text-muted)' : 'var(--accent-primary)' }}>
                                        {raceData.place === 'X' ? 'VOID' : ordinal(raceData.place)}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          );
                          });
                        })()}
                      </div>

                      {/* Cup Subtotals */}
                      <div style={{ 
                        marginTop: '0.75rem', 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(4, 1fr)', 
                        gap: '0.5rem',
                        padding: '0.5rem',
                        background: 'var(--bg-primary)',
                        borderRadius: 'var(--radius-sm)'
                      }}>
                        {TEAMS.map(team => {
                          // Calculate cup subtotal from races
                          const isMode12 = entry.mode === '12' || 
                            (entry.races[cup] && Object.keys(entry.races[cup]).length > 4);
                          const raceNumbers = isMode12 ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4];
                          const subtotal = raceNumbers.reduce((sum, raceNum) => {
                            const raceData = entry.races[cup][raceNum]?.[team];
                            if (!raceData) return sum; // Skip if no race data
                            const place = raceData.place;
                            if (place === 'X') return sum; // Skip void races
                            // Use unified place to points mapping
                            const points = placeToPoints(place);
                            return sum + points;
                          }, 0);

                          return (
                            <div key={team} style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>Team {team}</div>
                              <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--accent-primary)' }}>
                                {subtotal} pts
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </Collapse>
            </Card>
          );
        })}
          {hasMoreVisibleHistory && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0 1.25rem' }}>
              <Button onClick={loadMoreVisibleHistory} intent={Intent.NONE}>
                Load {VISIBLE_ACT_CHUNK} more ACTs
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Admin Password Dialog */}
      <Dialog
        isOpen={isAdminModalOpen}
        onClose={closeAdminModal}
        title="Admin Access Required"
        icon="lock"
        style={{ width: '400px' }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
      >
        <DialogBody>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              Enter the admin password to access edit and delete features:
            </p>
            <InputGroup
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Enter admin password..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAdminPasswordSubmit();
                }
              }}
              autoFocus
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={closeAdminModal}>
            Cancel
          </Button>
          <Button 
            intent={Intent.PRIMARY} 
            onClick={handleAdminPasswordSubmit}
            disabled={!adminPassword.trim()}
          >
            Access Admin Mode
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default History;
